import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import type { DemCustomer, DemMasterVersion, DemMasterVersionKey } from '../types/dem';

const CUSTOMER_SELECT =
  'code,name,address,address_2,postal,email,price_group,status,min_order,allowed_days,portal_token,is_blocked,salesperson_code,delivery_instruction,delivery_schedule,bill_to_customer_no';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

function normalizeAllowedDays(value: unknown): number[] {
  if (Array.isArray(value)) {
    return value.filter((d): d is number => typeof d === 'number' && d >= 0 && d <= 6);
  }
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value) as unknown;
      if (Array.isArray(parsed)) return normalizeAllowedDays(parsed);
    } catch {
      /* ignore */
    }
  }
  return [];
}

function rowToCustomer(row: Record<string, unknown>): DemCustomer {
  const minOrderRaw = row.min_order;
  let min_order = 0;
  if (typeof minOrderRaw === 'number') min_order = minOrderRaw;
  else if (minOrderRaw != null) {
    const parsed = Number(minOrderRaw);
    min_order = Number.isFinite(parsed) ? parsed : 0;
  }

  return {
    code: String(row.code ?? ''),
    name: String(row.name ?? ''),
    address: String(row.address ?? ''),
    address_2: String(row.address_2 ?? ''),
    postal: String(row.postal ?? '').trim() || extractPostalFromAddress(String(row.address ?? '')),
    email: String(row.email ?? ''),
    price_group: String(row.price_group ?? ''),
    status: String(row.status ?? ''),
    min_order,
    allowed_days: normalizeAllowedDays(row.allowed_days),
    portal_token: String(row.portal_token ?? ''),
    is_blocked: Boolean(row.is_blocked),
    salesperson_code: String(row.salesperson_code ?? '').trim(),
    delivery_instruction: String(row.delivery_instruction ?? '').trim(),
    delivery_schedule: String(row.delivery_schedule ?? '').trim(),
    bill_to_customer_no: String(row.bill_to_customer_no ?? '').trim(),
  };
}

/** Mon–Fri — NAV "DAILY" means weekday delivery (Sat optional elsewhere; Sun never). */
export const DEM_WEEKDAY_DAYS = [0, 1, 2, 3, 4] as const;

function daysEqual(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function isWeekdaysOnly(days: number[]): boolean {
  return daysEqual([...days].sort((a, b) => a - b), [...DEM_WEEKDAY_DAYS]);
}

/** Normalize common NAV typos before weekday parsing. */
function normalizeNavScheduleTypos(raw: string): string {
  return raw
    .replace(/\bTHRUS\b/gi, 'THURS')
    .replace(/\bFFRI\b/gi, 'FRI')
    .replace(/\bN\s*\.\s*A\.?\b/gi, 'N/A');
}

export function formatDeliveryDays(days: number[]): string {
  if (days.length === 0) return 'No Delivery';
  if (days.length === 7) return 'Everyday';
  if (isWeekdaysOnly(days)) return 'Weekdays';
  return [...days]
    .sort((a, b) => a - b)
    .map((d) => DAY_LABELS[d] ?? '')
    .filter(Boolean)
    .join(', ');
}

/**
 * Parse NAV / PowerBI delivery_schedule text → Mon=0 … Sun=6 (e.g. "TUES, THURS").
 * DAILY / EVERY DAY / ALL DAYS → Mon–Fri only (not Sat/Sun).
 */
export function parseNavDeliverySchedule(raw: string): number[] | null {
  const text = normalizeNavScheduleTypos(String(raw ?? '').trim());
  if (!text || text === '—') return null;

  const upper = text.toUpperCase();
  if (/\b(EVERY\s*DAY|EVERYDAY|DAILY|ALL\s*DAYS?)\b/.test(upper)) {
    return [...DEM_WEEKDAY_DAYS];
  }
  if (/\b(NO\s*DELIVERY|NONE|N\/?A)\b/.test(upper)) {
    return [];
  }

  const found = new Set<number>();
  const patterns: Array<{ idx: number; re: RegExp }> = [
    { idx: 0, re: /\bMON(?:DAY)?S?\b/gi },
    { idx: 1, re: /\bTUE(?:S(?:DAY)?)?S?\b/gi },
    { idx: 2, re: /\bWED(?:NESDAY)?S?\b/gi },
    { idx: 3, re: /\bTHU(?:R(?:S(?:DAY)?)?)?S?\b/gi },
    { idx: 4, re: /\bFRI(?:DAY)?S?\b/gi },
    { idx: 5, re: /\bSAT(?:URDAY)?S?\b/gi },
    { idx: 6, re: /\bSUN(?:DAY)?S?\b/gi },
  ];

  for (const { idx, re } of patterns) {
    re.lastIndex = 0;
    if (re.test(text)) found.add(idx);
  }

  if (found.size === 0) return null;
  return [...found].sort((a, b) => a - b);
}

export type DeliveryScheduleIssueKind = 'empty' | 'unparsed' | 'typo' | 'mismatch';

export type DeliveryScheduleIssue = {
  kind: DeliveryScheduleIssueKind;
  label: string;
};

export type DeliveryScheduleIssueRow = {
  customer: DemCustomer;
  issues: DeliveryScheduleIssue[];
  parsedDays: number[] | null;
};

/** Leftover tokens after stripping known day / daily / N/A words (catches unknown typos). */
function findSuspiciousScheduleTokens(raw: string): string[] {
  const stripped = raw
    .toUpperCase()
    .replace(/\b(EVERY\s*DAY|EVERYDAY|DAILY|ALL\s*DAYS?)\b/g, ' ')
    .replace(/\b(NO\s*DELIVERY|NONE|N\/?A|N\s*\.\s*A\.?)\b/g, ' ')
    .replace(/\bMON(?:DAY)?S?\b/g, ' ')
    .replace(/\bTUE(?:S(?:DAY)?)?S?\b/g, ' ')
    .replace(/\bWED(?:NESDAY)?S?\b/g, ' ')
    .replace(/\bTHU(?:R(?:S(?:DAY)?)?)?S?\b/g, ' ')
    .replace(/\bFRI(?:DAY)?S?\b/g, ' ')
    .replace(/\bSAT(?:URDAY)?S?\b/g, ' ')
    .replace(/\bSUN(?:DAY)?S?\b/g, ' ')
    .replace(/\bTHRUS\b/g, ' ')
    .replace(/\bFFRI\b/g, ' ')
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();
  if (!stripped) return [];
  return stripped.split(/\s+/).filter((w) => w.length >= 2);
}

/**
 * Flag Active-customer NAV schedules that need attention:
 * empty / unparsed / known typos / leftover tokens / saved days ≠ parsed NAV.
 */
export function getDeliveryScheduleIssues(
  deliverySchedule: string,
  allowedDays: number[],
): DeliveryScheduleIssue[] {
  const issues: DeliveryScheduleIssue[] = [];
  const raw = String(deliverySchedule ?? '').trim();

  if (!raw || raw === '—') {
    issues.push({ kind: 'empty', label: 'Empty NAV schedule' });
    return issues;
  }

  const typoNotes: string[] = [];
  if (/\bTHRUS\b/i.test(raw)) typoNotes.push('THRUS → THURS');
  if (/\bFFRI\b/i.test(raw)) typoNotes.push('FFRI → FRI');
  if (/\bN\s*\.\s*A\.?\b/i.test(raw)) typoNotes.push('N.A → N/A');
  for (const token of findSuspiciousScheduleTokens(raw)) {
    typoNotes.push(`Unrecognized “${token}”`);
  }
  if (typoNotes.length > 0) {
    issues.push({ kind: 'typo', label: typoNotes.join('; ') });
  }

  const parsed = parseNavDeliverySchedule(raw);
  if (parsed == null) {
    issues.push({ kind: 'unparsed', label: 'Could not parse NAV text' });
    return issues;
  }

  const saved = [...allowedDays].sort((a, b) => a - b);
  if (!daysEqual(parsed, saved)) {
    issues.push({
      kind: 'mismatch',
      label: `Saved (${formatDeliveryDays(saved)}) ≠ NAV (${formatDeliveryDays(parsed)})`,
    });
  }

  return issues;
}

export async function fetchActiveCustomersWithScheduleIssues(): Promise<DeliveryScheduleIssueRow[]> {
  const db = getDbSupabase('dem');
  const out: DeliveryScheduleIssueRow[] = [];
  const pageSize = 1000;
  let offset = 0;

  while (true) {
    const { data, error } = await db
      .from('customers')
      .select(CUSTOMER_SELECT)
      .eq('status', 'Active')
      .order('code', { ascending: true })
      .range(offset, offset + pageSize - 1);
    if (error) throw new Error(formatSupabaseError(error));
    const chunk = data ?? [];
    for (const row of chunk) {
      const customer = rowToCustomer(row as Record<string, unknown>);
      const issues = getDeliveryScheduleIssues(customer.delivery_schedule, customer.allowed_days);
      if (issues.length === 0) continue;
      out.push({
        customer,
        issues,
        parsedDays: parseNavDeliverySchedule(customer.delivery_schedule),
      });
    }
    if (chunk.length < pageSize) break;
    offset += pageSize;
  }

  return out;
}

export type DeliveryScheduleDisplay = {
  text: string;
  tone: 'neutral' | 'ok' | 'warn' | 'info';
};

export function getDeliveryScheduleDisplay(days: number[] | undefined): DeliveryScheduleDisplay {
  const allowed = days ?? [];
  if (allowed.length === 0) {
    return { text: '🚫 No Delivery Days', tone: 'warn' };
  }
  if (allowed.length === 7) {
    return { text: '🟢 Everyday', tone: 'ok' };
  }
  if (isWeekdaysOnly(allowed)) {
    return { text: '🟢 Weekdays', tone: 'ok' };
  }
  return { text: `📅 ${formatDeliveryDays(allowed)}`, tone: 'info' };
}

export function isDeliveryDateAllowed(isoDate: string, days: number[] | undefined): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return true;
  const allowed = days ?? [0, 1, 2, 3, 4, 5, 6];
  if (allowed.length === 0) return false;
  const weekday = (new Date(`${isoDate}T12:00:00`).getDay() + 6) % 7;
  return allowed.includes(weekday);
}

export function deliveryDateWeekdayLabel(isoDate: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return 'that day';
  return new Date(`${isoDate}T12:00:00`).toLocaleDateString('en-SG', { weekday: 'long' });
}

export function parseCustomerEmails(emailField: string): string[] {
  return emailField
    .split(/[,;]/)
    .map((e) => e.trim())
    .filter((e) => e.length > 0 && e.includes('@'));
}

export function joinCustomerEmails(emails: string[]): string {
  return emails.join(', ');
}

export function portalOrderUrl(token: string): string {
  return `https://banteckhan.com.sg/order/?token=${token}`;
}

/** List-cell preview only — full value stays in title / detail modal. */
function truncateEnd(value: string, max: number): string {
  if (value.length <= max) return value;
  return `${value.slice(0, Math.max(1, max - 1))}…`;
}

export function formatNameCellPreview(name: string): { text: string; title: string } {
  const clean = name.trim();
  if (!clean) return { text: '—', title: '' };
  return { text: truncateEnd(clean, 24), title: clean };
}

export function formatEmailCellPreview(email: string): { text: string; title: string } {
  const emails = parseCustomerEmails(email);
  if (emails.length === 0) return { text: '—', title: '' };
  return { text: 'yes', title: '' };
}

export function formatTokenCellPreview(token: string): { text: string; title: string } {
  if (!token.trim()) return { text: 'Not set', title: '' };
  if (token.length <= 10) return { text: token, title: token };
  return { text: `${token.slice(0, 4)}…${token.slice(-4)}`, title: token };
}

export function formatMoqCellPreview(minOrder: number): string {
  if (!Number.isFinite(minOrder) || minOrder <= 0) return '—';
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: 'SGD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(minOrder);
}

export function generatePortalToken(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 12);
  }
  return Math.random().toString(16).slice(2, 14);
}

/** DEM customer code before "-" — used to match CRM account code (e.g. P0325-01 → P0325). */
export function demMainCustomerCode(code: string): string {
  const trimmed = code.trim();
  return trimmed.includes('-') ? trimmed.split('-')[0]!.trim() : trimmed;
}

export function extractPostalFromAddress(address: string, postal = ''): string {
  const fromField = postal.trim();
  if (/^\d{6}$/.test(fromField)) return fromField;
  const match = address.match(/\b(\d{6})\b/);
  return match?.[1] ?? '';
}

/**
 * Display-only: join DEM address / address_2 / postal like the old single-line customer address.
 * Keeps DB columns split; use this wherever UI needs the full line (PO review, Manual ship-to, etc.).
 */
export function formatDemCustomerAddressDisplay(customer: {
  address?: string | null;
  address_2?: string | null;
  postal?: string | null;
}): string {
  const line1 = String(customer.address ?? '').trim();
  const line2 = String(customer.address_2 ?? '').trim();
  // Avoid "… RE&S BUILDING … RE&S BUILDING" when address_2 repeats a token already in address.
  const line2Useful =
    line2 &&
    !line1.toLowerCase().includes(line2.toLowerCase()) &&
    !normalizeLoose(line1).includes(normalizeLoose(line2));
  const joined = [line1, line2Useful ? line2 : ''].filter(Boolean).join(' ');
  const postal =
    extractPostalFromAddress(joined, String(customer.postal ?? '')) ||
    extractPostalFromAddress(line1, '') ||
    extractPostalFromAddress(line2, '');

  if (!postal) return joined;
  if (joined && /\b\d{6}\b/.test(joined)) return joined;
  if (!joined) return `SINGAPORE ${postal}`;
  if (/singapore\s*$/i.test(joined)) return `${joined} ${postal}`;
  return `${joined} SINGAPORE ${postal}`;
}

function normalizeLoose(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/** Append SG postal to address when missing — Lalamove portal shows address text only. */
export function formatLalamoveAddress(address: string, postal = ''): string {
  const trimmed = address.trim();
  const code = extractPostalFromAddress(trimmed, postal);
  if (!trimmed) return code ? `Singapore ${code}` : '';
  if (!code || /\b\d{6}\b/.test(trimmed)) return trimmed;
  return `${trimmed}, Singapore ${code}`;
}

/** Merge structured address parts for Lalamove Route / portal display. */
export function composeDeliveryAddress(parts: {
  address: string;
  unitNo?: string;
  building?: string;
  postal?: string;
}): string {
  const street = parts.address.trim();
  let unit = (parts.unitNo ?? '').trim();
  if (unit && !unit.startsWith('#')) unit = `#${unit}`;
  const building = (parts.building ?? '').trim();
  const postal = extractPostalFromAddress(street, parts.postal ?? '');

  const chunks = [street, unit, building].filter(Boolean);
  const base = chunks.join(', ');
  if (!postal) return base;
  if (base && /\b\d{6}\b/.test(base)) return base;
  if (!base) return `Singapore ${postal}`;
  return `${base}, Singapore ${postal}`;
}

export type DemCustomerSort = 'code_asc' | 'code_desc' | 'name_asc' | 'name_desc';
export type DemBlockedFilter = 'all' | 'unblocked' | 'blocked' | 'none';

export type FetchCustomersResult = {
  rows: DemCustomer[];
  total: number;
};

export async function fetchDemCustomers(options: {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: DemCustomerSort;
  blockedFilter?: DemBlockedFilter;
  /** Customers page: Active only. Orders / pickers: omit (all statuses). */
  statusFilter?: 'active' | 'all';
}): Promise<FetchCustomersResult> {
  const blockedFilter = options.blockedFilter ?? 'all';
  if (blockedFilter === 'none') {
    return { rows: [], total: 0 };
  }

  const page = Math.max(1, options.page ?? 1);
  const pageSize = Math.min(200, Math.max(25, options.pageSize ?? 50));
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  const search = options.search?.trim() ?? '';
  const sort = options.sort ?? 'code_asc';
  const statusFilter = options.statusFilter ?? 'all';

  const db = getDbSupabase('dem');
  let query = db.from('customers').select(CUSTOMER_SELECT, { count: 'exact' });
  if (statusFilter === 'active') {
    query = query.eq('status', 'Active');
  }

  if (search) {
    const escaped = search.replace(/[%_]/g, '\\$&');
    query = query.or(`code.ilike.%${escaped}%,name.ilike.%${escaped}%`);
  }

  if (blockedFilter === 'unblocked') {
    query = query.or('is_blocked.eq.false,is_blocked.is.null');
  } else if (blockedFilter === 'blocked') {
    query = query.eq('is_blocked', true);
  }

  const sortColumn = sort.startsWith('name') ? 'name' : 'code';
  const ascending = sort.endsWith('_asc');

  let orderedQuery = query.order(sortColumn, { ascending, nullsFirst: false });
  if (sortColumn !== 'code') {
    orderedQuery = orderedQuery.order('code', { ascending: true });
  }

  const { data, error, count } = await orderedQuery.range(from, to);
  if (error) throw new Error(formatSupabaseError(error));

  return {
    rows: (data ?? []).map((row) => rowToCustomer(row as Record<string, unknown>)),
    total: count ?? 0,
  };
}

const CUSTOMER_IN_CHUNK = 150;

/** Customers with DEM is_blocked (NAV Ship / Invoice / All). */
export async function fetchDemCustomersBlockedByCodes(codes: Iterable<string>): Promise<Set<string>> {
  const unique = [...new Set([...codes].map((c) => String(c ?? '').trim()).filter(Boolean))];
  const blocked = new Set<string>();
  if (unique.length === 0) return blocked;

  const db = getDbSupabase('dem');
  for (let i = 0; i < unique.length; i += CUSTOMER_IN_CHUNK) {
    const chunk = unique.slice(i, i + CUSTOMER_IN_CHUNK);
    const { data, error } = await db.from('customers').select('code,is_blocked').in('code', chunk);
    if (error) throw new Error(formatSupabaseError(error));
    for (const row of data ?? []) {
      const r = row as Record<string, unknown>;
      const code = String(r.code ?? '').trim();
      if (!code) continue;
      if (Boolean(r.is_blocked)) blocked.add(code);
    }
  }
  return blocked;
}

export async function fetchDemCustomer(code: string): Promise<DemCustomer | null> {
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('customers')
    .select(CUSTOMER_SELECT)
    .eq('code', code)
    .maybeSingle();

  if (error) throw new Error(formatSupabaseError(error));
  if (!data) return null;
  return rowToCustomer(data as Record<string, unknown>);
}

/** NAV salesperson code for a DEM customer (DEM table, else PowerBI via account-sales proxy). */
export async function fetchDemCustomerSalespersonCode(customerCode: string): Promise<string> {
  const code = customerCode.trim();
  if (!code) return '';

  try {
    const customer = await fetchDemCustomer(code);
    const fromDem = customer?.salesperson_code?.trim() ?? '';
    if (fromDem) return fromDem;
  } catch {
    // Column may not exist yet, or DEM read failed — fall through to PowerBI.
  }

  const headers: Record<string, string> = { Accept: 'application/json' };
  try {
    const { data } = await getDbSupabase('forms').auth.getSession();
    const token = data.session?.access_token?.trim();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    // proxy rejects unauthenticated
  }

  const response = await fetch(
    `/account-sales-api?resource=salesperson&mother_code=${encodeURIComponent(code)}`,
    { headers },
  );
  const json = (await response.json().catch(() => null)) as
    | { salesperson_code?: string; error?: string }
    | null;
  if (!response.ok) return '';
  return String(json?.salesperson_code ?? '').trim();
}

export type PowerBiCustomerDetail = {
  no: string;
  name: string;
  delivery_instruction: string;
  delivery_schedule: string;
  bill_to_customer_no: string;
  bill_to_name: string;
  shipment_address: string;
  billing_address: string;
  /** NAV Blocked: '', Ship, Invoice, All (or boolean-ish). */
  blocked: string;
};

/** Live NAV fields from PowerBI sales.customer_list (delivery schedule / instruction / bill-to). */
export async function fetchPowerBiCustomerDetail(
  customerNo: string,
): Promise<PowerBiCustomerDetail | null> {
  const no = customerNo.trim();
  if (!no) return null;

  const headers: Record<string, string> = { Accept: 'application/json' };
  try {
    const { data } = await getDbSupabase('dem').auth.getSession();
    const token = data.session?.access_token?.trim();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    // proxy rejects unauthenticated
  }

  const response = await fetch(
    `/cff-api?resource=customer_detail&no=${encodeURIComponent(no)}`,
    { headers },
  );
  const json = (await response.json().catch(() => null)) as
    | { customer?: PowerBiCustomerDetail | null; error?: string }
    | null;
  if (!response.ok) {
    throw new Error(json?.error || `PowerBI customer lookup failed (${response.status}).`);
  }
  const row = json?.customer;
  if (!row || !row.no) return null;
  return {
    no: String(row.no ?? '').trim(),
    name: String(row.name ?? '').trim(),
    delivery_instruction: String(row.delivery_instruction ?? '').trim(),
    delivery_schedule: String(row.delivery_schedule ?? '').trim(),
    bill_to_customer_no: String(row.bill_to_customer_no ?? '').trim(),
    bill_to_name: String(row.bill_to_name ?? '').trim(),
    shipment_address: String(row.shipment_address ?? '').trim(),
    billing_address: String(row.billing_address ?? '').trim(),
    blocked: String(row.blocked ?? '').trim(),
  };
}

export type UpdateDemCustomerPatch = {
  allowed_days?: number[];
  email?: string;
  min_order?: number;
  portal_token?: string;
};

export async function updateDemCustomer(
  code: string,
  patch: UpdateDemCustomerPatch,
): Promise<DemCustomer> {
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('customers')
    .update(patch)
    .eq('code', code)
    .select(CUSTOMER_SELECT)
    .maybeSingle();

  if (error) throw new Error(formatSupabaseError(error));
  if (!data) {
    throw new Error(
      'Update returned no rows — your account may not have write permission on customers yet.',
    );
  }
  return rowToCustomer(data as Record<string, unknown>);
}

const VERSION_KEYS: { key: DemMasterVersionKey; label: string }[] = [
  { key: 'customer_version', label: 'Customer Master' },
  { key: 'item_version', label: 'Item Master' },
  { key: 'price_version', label: 'Sales Price' },
  { key: 'standard_sales_lines_version', label: 'Standard Sales Lines' },
];

export async function countCustomersMissingPortalToken(): Promise<number> {
  const db = getDbSupabase('dem');
  const { count, error } = await db
    .from('customers')
    .select('code', { count: 'exact', head: true })
    .eq('status', 'Active')
    .or('portal_token.is.null,portal_token.eq.');

  if (error) throw new Error(formatSupabaseError(error));
  return count ?? 0;
}

async function fetchCustomerCodesMissingPortalToken(): Promise<string[]> {
  const db = getDbSupabase('dem');
  const codes: string[] = [];
  const chunk = 1000;
  let offset = 0;

  while (true) {
    const { data, error } = await db
      .from('customers')
      .select('code')
      .eq('status', 'Active')
      .or('portal_token.is.null,portal_token.eq.')
      .order('code')
      .range(offset, offset + chunk - 1);

    if (error) throw new Error(formatSupabaseError(error));
    if (!data?.length) break;

    for (const row of data) {
      if (row.code) codes.push(String(row.code));
    }

    if (data.length < chunk) break;
    offset += chunk;
  }

  return codes;
}

export async function autoGenerateMissingPortalTokens(
  onProgress?: (current: number, total: number) => void,
): Promise<number> {
  const codes = await fetchCustomerCodesMissingPortalToken();
  if (codes.length === 0) return 0;

  let updated = 0;
  for (let i = 0; i < codes.length; i += 1) {
    const code = codes[i]!;
    onProgress?.(i + 1, codes.length);
    const token = generatePortalToken();
    try {
      await updateDemCustomer(code, { portal_token: token });
      updated += 1;
    } catch {
      /* continue with next customer */
    }
  }

  return updated;
}

export async function fetchDemMasterVersions(): Promise<DemMasterVersion[]> {
  const db = getDbSupabase('dem');
  const keys = VERSION_KEYS.map((v) => v.key);
  const { data, error } = await db
    .from('system_config')
    .select('key, data')
    .in('key', keys);

  if (error) throw new Error(formatSupabaseError(error));

  const byKey = new Map<string, string | null>();
  for (const row of data ?? []) {
    const payload = row.data as { v?: string } | null;
    byKey.set(String(row.key), payload?.v != null ? String(payload.v) : null);
  }

  return VERSION_KEYS.map(({ key, label }) => ({
    key,
    label,
    version: byKey.get(key) ?? null,
  }));
}
